import { type DepreciationMethod, depreciationMethod } from '@libs/enums';
import type { OrEmpty } from '@ps-smart-cp/ppfw-client';
import type { DepreciationInfoFieldDisabledState } from '../types';

/**
 * 償却方法に応じて償却情報の各項目の非活性状態を判定する
 *
 * @param method 償却方法
 * @returns 項目ごとの非活性状態
 */
function judgeDepreciationInfoFieldDisabledState(
  method: OrEmpty<DepreciationMethod>,
): DepreciationInfoFieldDisabledState {
  const isMonthsDisabled =
    method === depreciationMethod.enum.twoHundredDclBalMethod ||
    method === depreciationMethod.enum.straightLineMethod;

  return {
    isLifeDisabled:
      method === depreciationMethod.enum.monthlyInstallmentDepreciation ||
      method === depreciationMethod.enum.laPeriodStraightLineMethod,
    isDepreciationMonthsDisabled: isMonthsDisabled,
    isRemainingMonthsDisabled: isMonthsDisabled,
    isRevisedAcquisitionValueDisabled: method !== depreciationMethod.enum.twoHundredDclBalMethod,
  };
}

type DisableDepreciationInfo = Readonly<{
  depreciationMethod: OrEmpty<DepreciationMethod>;
  life: string;
  depreciationMonths: string;
}>;

/**
 * 償却方法に応じて非活性となる償却情報の項目（耐用年数・償却月数）をブランクにする
 *
 * 自動判定で入力された値が、償却方法の変更により非活性項目に残ることを防ぐ
 *
 * @param info 償却情報
 * @returns 非活性項目をブランクにした償却情報
 */
function clearDisabledDepreciationInfoFields<T extends DisableDepreciationInfo>(info: T): T {
  const { isLifeDisabled, isDepreciationMonthsDisabled } = judgeDepreciationInfoFieldDisabledState(
    info.depreciationMethod,
  );

  return {
    ...info,
    life: isLifeDisabled ? '' : info.life,
    depreciationMonths: isDepreciationMonthsDisabled ? '' : info.depreciationMonths,
  };
}

export { clearDisabledDepreciationInfoFields, judgeDepreciationInfoFieldDisabledState };
